import { Component, ElementRef, OnInit, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgSelectModule } from '@ng-select/ng-select';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { OrganizationSearchComponent } from '../organization-search/organization-search.component';
import {
    AlertBannerComponent,
    EnterpriseDto,
    EnterpriseMembershipDto,
    EnterpriseMembershipStatus,
    EnterpriseType,
    FormatDateArrayPipe,
    FormattedDatePipe,
    OrganizationDto,
    IndexedDbService,
    OrganizationRelocationService,
    OrganizationRelocationStatus,
    getRelocationConfirmationMessageFor,
    getRelocationStatusMessageFor,
    relocationPendingConfirmationStatuses
} from 'lib';
import { EnterpriseGroupService } from '../../services/enterprise-group.service';
import { ToastrService } from 'ngx-toastr';
import { FaIconComponent, FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { HttpStatusCode } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { EnterpriseSharedService } from '../../services/enterprise-shared.service';
import { FormatInstanceStatusPipe } from '../../pipes/format-instance-status.pipe';
import { OrganizationRelocationComponent } from '../organization-relocation/organization-relocation.component';
import { finalize, Observable } from 'rxjs';

@Component({
    selector: 'mmoga-enterprise-form',
    standalone: true,
    imports: [ CommonModule, NgSelectModule, ReactiveFormsModule, OrganizationSearchComponent,
        FaIconComponent, AlertBannerComponent, FormatInstanceStatusPipe, OrganizationRelocationComponent ],
    templateUrl: './enterprise-form.component.html',
    styleUrl: './enterprise-form.component.scss',
    providers: [
        FormattedDatePipe,
        FormatDateArrayPipe
    ]
})
export class EnterpriseFormComponent implements OnInit {
    private formBuilder = inject(FormBuilder);
    private enterpriseGroupService = inject(EnterpriseGroupService);
    private toastrService = inject(ToastrService);
    private iconLibrary = inject(FaIconLibrary);
    private enterpriseSharedService = inject(EnterpriseSharedService);
    private router = inject(Router);
    private activatedRoute = inject(ActivatedRoute);
    private readonly organizationRelocationService = inject(OrganizationRelocationService);
    private readonly indexedDbService = inject(IndexedDbService);

    @ViewChild('groupNameInput') groupNameInput!: ElementRef;
    @ViewChild('aliasInput') aliasInput!: ElementRef;

    private pendingRelocationStorageKeyPrefix = 'pendingRelocations-';

    editedGroupId: number | null = null;
    editedGroup!: EnterpriseDto;
    showDuplicateNameAlert = false;
    showDuplicateAliasAlert = false;

    showOrganizationUsedAlert = false;
    organizationUsedAlertMessage = '';
    duplicateGroupNameAlertMessage = '';
    duplicateAliasAlert = '';
    updateInactiveGroupAlertMessage = '';
    currentGroupNameValue = '';
    currentAliasValue = '';
    enterpriseMemberships: EnterpriseMembershipDto[] = [];
    aliasStr = '';
    orgAdminDomainStr: string | undefined = '';
    showOrganizationRelocationModal = false;
    relocatingOrganization: OrganizationDto | undefined;
    relocatingOrganizationStatuses: Map<number, OrganizationRelocationStatus> = new Map<number, OrganizationRelocationStatus>();

    form: FormGroup = new FormGroup({
        groupName: new FormControl(''),
        alias: new FormControl('')
    });

    constructor() {
        this.iconLibrary.addIcons(faInfoCircle);
    }

    get groupName() {
        return this.form.get('groupName');
    }

    get alias() {
        return this.form.get('alias');
    }

    get isGroupInactive(): boolean {
        return !this.form.get('groupStatus')?.value;
    }

    get groupOrganizations() {
        return this.enterpriseMemberships.map(enterprise => enterprise.organization).reverse();
    }

    ngOnInit() {
        this.activatedRoute.parent?.params.subscribe(params => {
            this.editedGroupId = params['id'] ? +params['id'] : null;
        });

        this.form = this.formBuilder.group({
            groupName: [
                '',
                [
                    Validators.required,
                    Validators.maxLength(100),
                    Validators.pattern(/^(?! )(?:[a-zA-Z0-9'(),\-\s](?! $))*[a-zA-Z0-9'(),\-\s]$/)
                ]
            ],
            alias: [
                '',
                [
                    Validators.required,
                    Validators.maxLength(50),
                    Validators.pattern(/^[A-Za-z0-9/-]+$/)
                ]
            ],
            groupStatus: true
        });
        this.form.controls['groupName'].valueChanges.subscribe(() => {
            this.showDuplicateNameAlert = false;
        });
        this.form.controls['alias'].valueChanges.subscribe(() => {
            this.showDuplicateAliasAlert = false;
        });

        if (this.editedGroupId) {
            this.enterpriseSharedService.loadEnterprise(this.editedGroupId).subscribe({
                next: group => {
                    this.editedGroup = group;
                    this.orgAdminDomainStr = group.orgAdminDomain;
                    this.aliasStr = group.alias;
                    this.form.patchValue({
                        groupName: group.name,
                        alias: group.alias,
                        groupStatus: !group.disabled
                    });

                    this.enterpriseMemberships = group.enterpriseMemberships ?? [];
                    this.loadPendingRelocations();
                }
            });
        }
    }

    cancel(): void {
        this.router.navigate([ '/home/groups' ]);
    }

    save(): void {
        if (this.form.valid) {
            let enterpriseDto: Partial<EnterpriseDto>;
            if (this.editedGroupId) {
                enterpriseDto = this.updateEnterpriseDto();
            } else {
                enterpriseDto = this.createEnterpriseDto();
            }

            this.enterpriseGroupService.createEnterprise(enterpriseDto).subscribe({
                next: (savedEnterprise) => {
                    this.toastrService.success('The information was successfully saved', 'Enterprise group');
                    this.enterpriseSharedService.updateEnterpriseName(savedEnterprise.name);
                    this.router.navigate([ '/group-management', savedEnterprise.id, 'details' ]);
                },
                error: (error) => {
                    if (error.status === HttpStatusCode.Conflict) {

                        if (error.error.message?.includes('DUPLICATE_NAME')) {
                            this.currentGroupNameValue = this.groupName?.value;
                            this.groupNameInput.nativeElement.focus();
                            this.duplicateGroupNameAlertMessage = 'The group name "' + this.currentGroupNameValue
                                + '" already exists. Please enter a unique group name.';
                            this.showDuplicateNameAlert = true;
                        } else if (error.error.message?.includes('DUPLICATE_ALIAS')) {
                            this.currentAliasValue = this.alias?.value;
                            this.aliasInput.nativeElement.focus();
                            this.duplicateAliasAlert = 'The organization Amin URL "' + this.currentAliasValue
                                + '" already exists. Please enter a unique Organization Amin URL.';
                            this.showDuplicateAliasAlert = true;
                        }
                    }
                    if (error.status === HttpStatusCode.PreconditionFailed) {
                        this.showOrganizationUsedAlert = true;
                        this.organizationUsedAlertMessage = error.error.message;
                    }
                    if (error.status === HttpStatusCode.BadRequest) {
                        this.updateInactiveGroupAlertMessage =
                            'Can not update an inactive Enterprise/MSO besides removing linked instances.';
                    }
                }
            });
        }
    }

    addOrganization(organization: OrganizationDto): void {
        const organizationExists = this.enterpriseMemberships.some(
            (enterprise) => enterprise.organization?.id === organization.id
        );
        if (!organizationExists) {
            this.enterpriseMemberships.push({
                organization: organization,
                status: EnterpriseMembershipStatus.PENDING
            } as EnterpriseMembershipDto);
        } else {
            this.toastrService.warning('Organization already added');
        }
    }

    moveOrganization(organization: OrganizationDto): void {
        this.relocatingOrganization = organization;
        this.showOrganizationRelocationModal = true;
    }

    handleOrganizationRelocation(isMoving: boolean): void {
        this.showOrganizationRelocationModal = false;
        if (isMoving && this.relocatingOrganization && this.editedGroupId) {
            this.addPendingRelocationIfNotAlready(this.editedGroupId, this.relocatingOrganization);
            this.loadPendingRelocations();
        } else {
            this.relocatingOrganization = undefined;
        }
    }

    loadPendingRelocations(): void {
        if (!this.editedGroupId || this.enterpriseMemberships.length === 0) {
            return;
        }
        const enterpriseId = this.editedGroupId;
        const organizations = this.enterpriseMemberships.map(em => em.organization);
        this.organizationRelocationService.getStatuses(enterpriseId, organizations.map(org => org.id))
            .pipe(finalize(() => this.sortMemberships()))
            .subscribe({
                next: relocationStatuses => {
                    relocationStatuses.forEach(statusDto => {
                        this.relocatingOrganizationStatuses.set(statusDto.organizationRelocationRequestDto.organizationId, statusDto.status);
                        const relocatingOrganization = organizations
                            .find(org => org.id === statusDto.organizationRelocationRequestDto.organizationId);
                        if (relocatingOrganization) {
                            this.addPendingRelocationIfNotAlready(enterpriseId, relocatingOrganization);
                        }
                    });
                    this.validateIfRelocationCompletedOrFailed(enterpriseId, organizations.map(org => org.id));
                },
                error: () => this.toastrService.error('Failed to get pending site relocations for the group.')
            });
    }

    addPendingRelocationIfNotAlready(enterpriseId: number, relocatingOrganization: OrganizationDto) {
        this.indexedDbService.addDataIfNotExists(this.getRelocationStorageKey(enterpriseId), relocatingOrganization).subscribe();
    }

    validateIfRelocationCompletedOrFailed(enterpriseId: number, organizationIds: number[]) {
        this.retrievePendingRelocatingOrganizations(enterpriseId).subscribe({
            next: (relocatingOrganizations) => {
                for (const relocatingOrg of relocatingOrganizations) {
                    const noOrgRelocationStatus = !this.relocatingOrganizationStatuses.get(relocatingOrg.id);
                    if (!organizationIds.includes(relocatingOrg.id) && noOrgRelocationStatus) {
                        this.toastrService.success(`Successfully moved ${relocatingOrg.name} to another organization.`);
                        this.removeRelocatingOrganization(enterpriseId, relocatingOrg.id);
                    } else if (organizationIds.includes(relocatingOrg.id) && noOrgRelocationStatus) {
                        this.toastrService.error(`Failed to move ${relocatingOrg.name}.`);
                        this.removeRelocatingOrganization(enterpriseId, relocatingOrg.id);
                    }
                }
            },
            error: () => this.toastrService.error('Failed to retrieve relocating organizations.')
        });
    }

    retrievePendingRelocatingOrganizations(enterpriseId: number): Observable<OrganizationDto[]> {
        return this.indexedDbService.getAllData<OrganizationDto>(this.getRelocationStorageKey(enterpriseId));
    }

    removeRelocatingOrganization(enterpriseId: number, relocatingOrgId: number): void {
        this.indexedDbService.deleteData(this.getRelocationStorageKey(enterpriseId), relocatingOrgId).subscribe();
    }

    isRelocationInConfirmation(organizationId: number): boolean {
        const status = this.relocatingOrganizationStatuses.get(organizationId);
        return !!status && relocationPendingConfirmationStatuses.has(status);
    }

    isRelocationNotRejected(organizationId: number): boolean {
        const status = this.relocatingOrganizationStatuses.get(organizationId);
        return !!status && OrganizationRelocationStatus.REJECTED !== status;
    }

    isMoveDisabled(member: EnterpriseMembershipDto): boolean {
        return member.status === EnterpriseMembershipStatus.PENDING;
    }

    getRelocationConfirmationMessage(organizationId: number): string {
        return getRelocationConfirmationMessageFor(this.relocatingOrganizationStatuses.get(organizationId));
    }

    getRelocationStatusMessage(organizationId: number): string {
        return getRelocationStatusMessageFor(this.relocatingOrganizationStatuses.get(organizationId));
    }

    hasOrganizationsAdded(): boolean {
        return this.enterpriseMemberships?.length !== 0;
    }

    sortMemberships(): void {
        const items = this.enterpriseMemberships ?? [];
        this.enterpriseMemberships = [...items].sort((a, b) => {
            if (this.relocatingOrganizationStatuses.size > 0) {
                const aId = a?.organization?.id;
                const bId = b?.organization?.id;

                const aIsRelocating = !!aId && this.isRelocationNotRejected(aId);
                const bIsRelocating = !!bId && this.isRelocationNotRejected(bId);
                if (aIsRelocating !== bIsRelocating) {
                    return aIsRelocating ? -1 : 1;
                }

                const aStatusText = this.getRelocationStatusMessage(aId).toLowerCase();
                const bStatusText = this.getRelocationStatusMessage(bId).toLowerCase();
                return aStatusText.localeCompare(bStatusText);
            }
            const aUrlPrefix = (a?.organization?.urlPrefix ?? '').toLowerCase();
            const bUrlPrefix = (b?.organization?.urlPrefix ?? '').toLowerCase();
            return aUrlPrefix.localeCompare(bUrlPrefix);
        });
    }

    private createEnterpriseDto(): Partial<EnterpriseDto> {
        const groupOrganizations = [] as EnterpriseMembershipDto[];
        this.enterpriseMemberships?.forEach(enterpriseMember => {
            if (enterpriseMember.organization) {
                groupOrganizations.push({
                    organization: {
                        id: enterpriseMember.organization.id
                    },
                    status: EnterpriseMembershipStatus.PENDING  // Use existing status or default to PENDING
                } as EnterpriseMembershipDto);
            }
        });

        return {
            name: this.groupName?.value,
            type: EnterpriseType.CORP,
            alias: this.alias?.value,
            disabled: !this.form.get('groupStatus')?.value,
            enterpriseMemberships: groupOrganizations
        };
    }

    private updateEnterpriseDto(): Partial<EnterpriseDto> {
        const enterpriseDto = this.editedGroup;
        enterpriseDto.name = this.groupName?.value;
        enterpriseDto.disabled = !this.form.get('groupStatus')?.value;
        enterpriseDto.enterpriseMemberships = this.enterpriseMemberships.map(enterprise => ({
            organization: {
                id: enterprise.organization?.id
            },
            status: enterprise.status,
            confirmedBy: enterprise.confirmedBy,
            confirmedDatetime: enterprise.confirmedDatetime
        })) as EnterpriseMembershipDto[];
        return enterpriseDto;
    }

    private getRelocationStorageKey(enterpriseId: number) {
        return this.pendingRelocationStorageKeyPrefix + enterpriseId;
    }
}
